import { useRoute } from '@react-navigation/native';
import React, { useMemo } from 'react';
import { type StyleProp, type ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { imgEmptyList } from '@/assets/images';
import { Divider, Scroll, Stack } from '@/components/layout';
import { Avatar, Image, Loading, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import GuardianScreenHeader from '@/features/guardian-player/components/guardian-screen-header';
import { SessionDetailLevelCard } from '@/features/guardian-player/components/player-session-details';
import { useGuardianPlayerSessionDetailQuery } from '@/features/guardian-player/queries';
import {
  buildGuardianSessionDetailLevelSections,
  formatGuardianApiDate,
} from '@/features/guardian-player/utils';
import { createStyles, useGuardianStackNavigation, useScaling } from '@/hooks';
import type { GuardianStackScreenProps } from '@/navigations/types';
import { colors, guardianColors } from '@/theme/colors';

type SessionDetailRoute = GuardianStackScreenProps<'PlayerSessionDetail'>['route'];
type PlayerSessionDetailContentProps = {
  playerId: string;
  sessionId?: string;
  contentContainerStyle?: StyleProp<ViewStyle>;
};

const strings = STRINGS.guardian.playerSessionDetails;

export function PlayerSessionDetailContent({
  playerId,
  sessionId,
  contentContainerStyle,
}: PlayerSessionDetailContentProps) {
  const styles = useStyles();
  const { scale } = useScaling();
  const insets = useSafeAreaInsets();
  const { data, isLoading } = useGuardianPlayerSessionDetailQuery(playerId, sessionId ?? '');
  const levelSections = useMemo(() => buildGuardianSessionDetailLevelSections(data), [data]);
  const isSessionProgressEmpty = data
    ? data.pins.length === 0 && data.attempts.length === 0
    : false;

  const coachAvatarSource = data?.coach.avatarUrl ? { uri: data.coach.avatarUrl } : undefined;
  const sessionDate = formatGuardianApiDate(data?.date, 'EEE MMM d, yyyy', data?.date);

  const content = isLoading ? (
    <Stack style={styles.loadingState}>
      <Loading />
    </Stack>
  ) : data ? (
    <>
      <Stack backgroundColor={colors['0']} gap={scale(10)} style={styles.coachCard}>
        <Stack direction="row" align="center" gap={scale(8)}>
          <Avatar size={scale(32)} borderWidth={1} borderColor={guardianColors['3']}>
            <Avatar.Image source={coachAvatarSource} />
          </Avatar>
          <Text
            variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
            numberOfLines={1}
            style={styles.coachName}>
            {data.coach.name}
          </Text>
        </Stack>
        <Divider color={guardianColors['3']} />
        <Text variant="Font Guardian App/Subtitle/Subtitle 11 Regular" style={styles.sessionDate}>
          {sessionDate}
        </Text>
      </Stack>

      {isSessionProgressEmpty ? (
        <Stack align="center" justify="center" style={styles.emptyState}>
          <Image
            source={imgEmptyList}
            width={scale({ phone: 100, tablet: 120 })}
            height={scale({ phone: 90, tablet: 96 })}
            resizeMode="contain"
          />
          <Text variant="Font Guardian App/Subtitle/Subtitle 14 Bold" style={styles.emptyTitle}>
            {strings.emptyTitle}
          </Text>
        </Stack>
      ) : (
        <>
          <Text
            variant="Font Guardian App/Subtitle/Subtitle 11 Semibold"
            style={styles.sectionLabel}>
            {strings.skillLearned}
          </Text>

          <Stack gap={scale(12)}>
            {levelSections.map(section => (
              <SessionDetailLevelCard
                key={section.level}
                level={section.level}
                pins={section.pins}
                attempts={section.attempts}
                sessionDate={data.date}
              />
            ))}
          </Stack>
        </>
      )}
    </>
  ) : null;

  return (
    <Scroll
      showsVerticalScrollIndicator={false}
      contentContainerStyle={[
        styles.content,
        { paddingBottom: insets.bottom + scale(12) },
        contentContainerStyle,
      ]}>
      {content}
    </Scroll>
  );
}

export default function PlayerSessionDetailScreen() {
  const route = useRoute<SessionDetailRoute>();
  const navigation = useGuardianStackNavigation();
  const { player, sessionId } = route.params;

  return (
    <Stack flex={1} backgroundColor={guardianColors['1']}>
      <GuardianScreenHeader title={strings.screenTitle} onBack={navigation.goBack} />
      <PlayerSessionDetailContent playerId={player.id} sessionId={sessionId} />
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  content: {
    paddingHorizontal: scale(20),
  },
  coachCard: {
    borderRadius: scale(12),
    borderCurve: 'continuous',
    padding: scale({ phone: 12, tablet: 24 }),
  },
  coachName: {
    color: guardianColors['10'],
    flex: 1,
  },
  sessionDate: {
    color: guardianColors['6'],
  },
  sectionLabel: {
    color: guardianColors['6'],
    marginBottom: scale(12),
    marginTop: scale({ phone: 12, tablet: 24 }),
  },
  loadingState: {
    paddingVertical: scale(64),
  },
  emptyState: {
    paddingTop: scale({ phone: 80, tablet: 120 }),
    paddingHorizontal: scale(20),
  },
  emptyTitle: {
    color: guardianColors['6'],
    marginTop: scale(10),
    textAlign: 'center',
  },
}));
